import React, { useState } from 'react';
import styled from 'styled-components';
import { Trash } from '@styled-icons/boxicons-regular/Trash';
import { formatDistanceToNow } from 'date-fns';
import useTranslation from 'next-translate/useTranslation';
import { style, User, Comment as CommentType, useComment } from '../../../api';

import ReplyForm from './ReplyForm';
import { ProfileLinkStyled } from '../../common/Link';
import {
  NoMobile,
  Mobile,
  mobile,
  tablet,
  desktop,
  noDesktop,
} from '../../../style/constants';
import UserImage from '../../common/UserImage';
import { TextBold12, TextRegular14, TextRegular12 } from '../../common/Text';
import { HeartBorder, Heart } from '../../common/Icons';
import useLoginWithRedirect from '../../../hooks/useLoginWithRedirect';
import { Button } from '../../../VuexyDesign/atoms/_index';

const Comment = ({
  user,
  id,
  trackId,
  userIsLoggedIn = false,
  withReply = userIsLoggedIn,
  userId,
  deleteComment,
  parentCommentId,
}: {
  user: User;
  id: number;
  replies: CommentType[];
  trackId: number;
  userIsLoggedIn?: boolean;
  withReply?: boolean;
  userId?: number;
  deleteComment: (
    commentId: number,
    parent: {
      parentCommentId?: number;
      trackId?: number;
    },
  ) => void;
  parentCommentId?: number;
}) => {
  const { t } = useTranslation('tracks');
  const [showReplyForm, setShowReplyForm] = useState(false);
  const redirect = useLoginWithRedirect(false);

  const { data: comment, like, unlike } = useComment(id);

  if (!comment) {
    return null;
  }

  const {
    comment: message,
    user_liked_comment,
    feedback,
    like_count: likes,
    timestamp,
    replies,
  } = comment;

  function deleteCommentHandler() {
    deleteComment(id, { parentCommentId, trackId });
  }

  return (
    <Outer>
      <Container>
        <ImgBox>
          <ProfileLinkStyled profileId={user.id}>
            <UserImage src={user.profile_photo} size={22} />
          </ProfileLinkStyled>
          <ProfileLinkStyled profileId={user.id}>
            <TextBold12 style={{ marginLeft: 12 }}>
              {user.display_name}
            </TextBold12>
          </ProfileLinkStyled>
          {feedback && (
            <FeedbackLabel>{t('settings:feedback') as string}</FeedbackLabel>
          )}
          <TextRegular12
            style={{
              marginLeft: 4,
              color: style.grey_dark_new,
            }}
          >
            {formatDistanceToNow(new Date(timestamp * 1000))} ago
          </TextRegular12>
          <Likes
            likes={likes}
            onClick={async () => {
              await redirect();
              if (user_liked_comment) {
                unlike();
              } else {
                like();
              }
            }}
            liked={user_liked_comment}
          />
        </ImgBox>
        <Col>
          <CommentContent>
            <CommentData>
              <MessageWrapper>
                <Message>{message}</Message>
                {withReply && (
                  <ReplyDeleteWrapper>
                    <ReplyButton
                      outline
                      color="dark"
                      onClick={() => setShowReplyForm(!showReplyForm)}
                      className="comment-reply"
                    >
                      {t('tracks:reply') as string}
                    </ReplyButton>
                    {userId === user.id && (
                      <>
                        <NoMobile>
                          <Delete
                            outline
                            size="sm"
                            className="text-dark"
                            onClick={() => deleteCommentHandler()}
                            text={t('tracks:delete') as string}
                          />
                        </NoMobile>
                        <Mobile>
                          <Trash
                            onClick={() => deleteCommentHandler()}
                            size={16}
                            style={{ marginRight: -2 }}
                          />
                        </Mobile>
                      </>
                    )}
                  </ReplyDeleteWrapper>
                )}
              </MessageWrapper>
              {withReply && showReplyForm && (
                <ReplyForm
                  commentId={id}
                  hide={() => setShowReplyForm(false)}
                />
              )}
            </CommentData>
          </CommentContent>
        </Col>
      </Container>
      {!!replies.length && (
        <RepliesContainer>
          {replies.map((reply) => (
            <Comment
              {...reply}
              key={reply.id}
              withReply={false}
              userId={userId}
              trackId={trackId}
              deleteComment={deleteComment}
              parentCommentId={id}
            />
          ))}
        </RepliesContainer>
      )}
    </Outer>
  );
};

const MessageWrapper = styled.div`
  display: flex;
`;

const Likes = styled(
  ({
    likes,
    onClick,
    className,
    liked = false,
  }: {
    likes: number;
    onClick?: () => void;
    className?: string;
    liked?: boolean;
  }) => (
    <LikesWrapper onClick={onClick} className={className}>
      {likes > 0 && <TextRegular14>{likes}</TextRegular14>}
      {liked ? (
        <Heart size={14} color={style.primary_pink_new} />
      ) : (
        <HeartBorder size={14} />
      )}
    </LikesWrapper>
  ),
)``;

const LikesWrapper = styled.div`
  display: flex;
  align-items: center;

  cursor: pointer;

  svg {
    margin-left: 4px;
  }
`;

const ReplyButton = styled(Button)`
  font-size: 11px;
  white-space: nowrap;
  min-height: 20px !important;
  padding: 3px 12px !important;
  font-weight: 700;
`;

const Outer = styled.div`
  word-break: break-word;
  padding-right: 10px;
`;

const RepliesContainer = styled.div`
  padding-top: 10px;
  @media ${tablet} {
    padding-left: 75px;
  }
  @media ${mobile} {
    padding-left: 25px;
  }
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  border-bottom: 1px solid ${style.grey_light_1_new};
  padding-top: 10px;
  padding-bottom: 12px;
`;

const CommentContent = styled.div`
  display: flex;
  flex-direction: row;
  font-size: 12px;
`;

const CommentData = styled.div`
  word-break: break-word;
  width: 100%;
`;

const Delete = styled(Button)`
  font-size: 11px;
  white-space: nowrap;
  min-height: 20px !important;
  padding: 3px 12px !important;
  font-weight: 700;
  border: 1.5px solid #d8d8d8 !important;
`;

const ImgBox = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 8px;

  ${Likes} {
    margin-left: auto;
  }
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const Message = styled(TextRegular14)`
  color: ${style.black};
  word-wrap: break-word;

  @media ${desktop} {
    flex-basis: 90%;
  }

  @media ${noDesktop} {
    flex-basis: 80%;
  }
`;

const FeedbackLabel = styled.div`
  text-transform: uppercase;
  color: white;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 2px 6px;
  margin-left: 6px;
  margin-right: 4px;
  font-size: 10px;

  background-color: ${style.primary_pink_new};
`;

const ReplyDeleteWrapper = styled.div`
  display: flex;
  height: 20px;
  gap: 5px;

  @media ${desktop} {
    align-items: flex-end;
  }

  @media ${noDesktop} {
    justify-content: flex-end;
    svg {
      margin-left: 4px;
    }
  }
  margin-left: auto;
`;

export default Comment;
